import React, { useState, useEffect, useCallback } from 'react';
import { getSites, createSite, updateSite, deleteSite, assignSiteModerator, getModerators } from '../lib/api';
import LocationPicker from '../components/common/LocationPicker';
import { useWebSocket } from '../context/WebSocketContext';
import usePageTitle from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MapPin, Plus, MoreHorizontal, Pencil, Trash2, Zap, UserCog, Wifi, WifiOff, FileSpreadsheet } from 'lucide-react';
import AppChargesExcelDialog from '../components/reports/AppChargesExcelDialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Sites = () => {
  usePageTitle('Stacionet');
  
  const [sites, setSites] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedModerator, setSelectedModerator] = useState('');
  const [editingSite, setEditingSite] = useState(null);
  const [excelTarget, setExcelTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    latitude: '',
    longitude: '',
    description: ''
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedSites = [...sites].sort((a, b) => {
    let aVal = sortField === 'name' ? (a.name || '') : (a.charger_count || 0);
    let bVal = sortField === 'name' ? (b.name || '') : (b.charger_count || 0);
    if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal, 'sq') : bVal.localeCompare(aVal, 'sq');
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const fetchSites = useCallback(async () => {
    try {
      const response = await getSites();
      setSites((response.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sq')));
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      toast.error('Gabim në ngarkimin e stacioneve');
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket connection
  const { connected: wsConnected, subscribe } = useWebSocket();
  
  // Handle WebSocket messages for real-time updates
  useEffect(() => {
    const unsubscribe = subscribe('*', (event) => {
      const eventType = event.type;
      
      switch (eventType) {
        case 'site.created':
        case 'site.updated':
        case 'site.deleted':
        case 'charger.created':
        case 'charger.deleted':
        case 'stats.refresh':
          fetchSites();
          break;
        default:
          break;
      }
    });
    return () => unsubscribe();
  }, [subscribe, fetchSites]);

  const fetchModerators = async () => {
    try {
      const response = await getModerators();
      const mods = (response.data.items || response.data || []).filter(u => u.is_active);
      setModerators(mods);
    } catch (error) {
      console.error('Failed to fetch moderators:', error);
    }
  };

  useEffect(() => {
    fetchSites();
    if (isAdmin) {
      fetchModerators();
    }
  }, [isAdmin]);

  const handleOpenDialog = (site = null) => {
    if (site) {
      setEditingSite(site);
      setFormData({
        name: site.name,
        address: site.address,
        city: site.city,
        latitude: site.latitude.toString(),
        longitude: site.longitude.toString(),
        description: site.description || ''
      });
    } else {
      setEditingSite(null);
      setFormData({
        name: '',
        address: '',
        city: '',
        latitude: '',
        longitude: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };

      if (editingSite) {
        await updateSite(editingSite.id, data);
        toast.success('Stacioni u përditësua');
      } else {
        await createSite(data);
        toast.success('Stacioni u krijua');
      }
      setDialogOpen(false);
      fetchSites();
    } catch (error) {
      console.error('Failed to save site:', error);
      toast.error('Gabim në ruajtjen e stacionit');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt që dëshironi ta fshini këtë stacion?')) return;
    try {
      await deleteSite(id);
      toast.success('Stacioni u fshi');
      fetchSites();
    } catch (error) {
      console.error('Failed to delete site:', error);
      toast.error('Gabim në fshirjen e stacionit');
    }
  };

  const handleOpenAssignDialog = (site) => {
    setSelectedSite(site);
    setSelectedModerator(site.assigned_moderator_id || 'none');
    setAssignDialogOpen(true);
  };

  const handleAssignModerator = async () => {
    try {
      const moderatorId = selectedModerator === 'none' ? null : selectedModerator;
      await assignSiteModerator(selectedSite.id, moderatorId);
      toast.success(moderatorId ? 'Moderatori u caktua me sukses' : 'Moderatori u hoq');
      setAssignDialogOpen(false);
      setSelectedSite(null);
      await fetchSites();
    } catch (error) {
      console.error('Failed to assign moderator:', error);
      toast.error('Gabim në caktimin e moderatorit');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-48" />
        <div className="skeleton h-96 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sites-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-outfit">Stacionet e Karikimit</h1>
          <p className="text-zinc-400 mt-1">Menaxhoni stacionet e karikimit</p>
        </div>
        {isAdmin && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-site-btn">
            <Plus className="w-4 h-4 mr-2" />
            Shto Stacion
          </Button>
        )}
      </div>

      <Card className="bg-zinc-900/50 border-white/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => handleSort('name')}>
                  <div className="flex items-center gap-1">
                    Emri
                    <span className="text-xs">{sortField === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                  </div>
                </TableHead>
                <TableHead className="text-zinc-400">Adresa</TableHead>
                <TableHead className="text-zinc-400">Qyteti</TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => handleSort('charger_count')}>
                  <div className="flex items-center gap-1">
                    Karikues
                    <span className="text-xs">{sortField === 'charger_count' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                  </div>
                </TableHead>
                {isAdmin && <TableHead className="text-zinc-400">Moderatori</TableHead>}
                <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 6 : 5} className="text-center text-zinc-500 py-12">
                    <MapPin className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                    <p>Nuk ka stacione të regjistruara</p>
                  </TableCell>
                </TableRow>
              ) : (
                sortedSites.map((site) => (
                  <TableRow key={site.id} className="border-white/5 hover:bg-white/5">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{site.name}</p>
                          <code className="id-field text-xs">{site.id.slice(0, 8)}</code>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-300">{site.address}</TableCell>
                    <TableCell className="text-zinc-300">{site.city}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-zinc-500" />
                        <span className="text-zinc-300">{site.charger_count}</span>
                      </div>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {site.assigned_moderator ? (
                          <div className="flex items-center gap-2">
                            <UserCog className="w-4 h-4 text-emerald-500" />
                            <span className="text-zinc-300 text-sm">{site.assigned_moderator.full_name}</span>
                          </div>
                        ) : (
                          <span className="text-zinc-500 text-sm">Pa caktuar</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="action-btn">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
                          <DropdownMenuItem 
                            onClick={() => navigate(`/chargers?site=${site.id}`)}
                            className="text-zinc-300 focus:bg-white/10 focus:text-white"
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            Shiko Karikuesit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setExcelTarget({ id: site.id, name: site.name })}
                            className="text-zinc-300 focus:bg-white/10 focus:text-white"
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Eksporto Excel
                          </DropdownMenuItem>
                          {isAdmin && (
                            <>
                              <DropdownMenuItem 
                                onClick={() => handleOpenAssignDialog(site)}
                                className="text-zinc-300 focus:bg-white/10 focus:text-white"
                              >
                                <UserCog className="w-4 h-4 mr-2" />
                                Cakto Moderator
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleOpenDialog(site)}
                                className="text-zinc-300 focus:bg-white/10 focus:text-white"
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifiko
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(site.id)}
                                className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Fshi
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">
              {editingSite ? 'Modifiko Stacionin' : 'Shto Stacion të Ri'}
            </DialogTitle>
            <DialogDescription>
              Kërkoni adresën ose klikoni direkt në hartë për të vendosur lokacionin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Emri i Stacionit</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Stacioni Karikimi Qendror"
                required
                data-testid="site-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Vendndodhja në Hartë</Label>
              <LocationPicker
                value={{
                  address: formData.address,
                  latitude: formData.latitude,
                  longitude: formData.longitude
                }}
                onChange={(location) => {
                  setFormData({
                    ...formData,
                    address: location.address,
                    city: location.city || formData.city,
                    latitude: location.latitude.toString(),
                    longitude: location.longitude.toString()
                  });
                }}
                placeholder="Kërkoni adresën ose klikoni në hartë..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">Qyteti</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Tiranë"
                required
                data-testid="site-city-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Përshkrimi</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Përshkrimi i stacionit..."
                rows={3}
                data-testid="site-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Anulo
              </Button>
              <Button type="submit" data-testid="save-site-btn">
                {editingSite ? 'Ruaj Ndryshimet' : 'Krijo Stacionin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Moderator Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">
              Cakto Moderator
            </DialogTitle>
            <DialogDescription>
              Zgjidhni moderatorin përgjegjës për stacionin &quot;{selectedSite?.name}&quot;
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Moderatori</Label>
              <Select value={selectedModerator} onValueChange={setSelectedModerator}>
                <SelectTrigger className="bg-zinc-800 border-white/10">
                  <SelectValue placeholder="Zgjidhni moderatorin..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="none" className="text-zinc-400">Pa moderator</SelectItem>
                  {moderators.map((mod) => (
                    <SelectItem key={mod.id} value={mod.id} className="text-zinc-300">
                      {mod.full_name} ({mod.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Anulo
            </Button>
            <Button onClick={handleAssignModerator}>
              Ruaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppChargesExcelDialog
        open={!!excelTarget}
        onOpenChange={(open) => { if (!open) setExcelTarget(null); }}
        scope="site"
        targetId={excelTarget?.id}
        targetName={excelTarget?.name}
      />
    </div>
  );
};

export default Sites;
